import React, { useEffect, useCallback } from 'react'

import { browserHistory } from 'react-router'

import { client } from 'config/routes'

import { IngredientIssuesPresentation } from './IngredientIssues.presentation'

interface IngredientIssuesProps {
  content: {
    title: string
    body: string
    button1Copy: string
  }
  fetchIngredientIssues: () => void
  ingredients: Record<string, IngredientInfo>
  issues: IssueType[]
  storeSelectedIngredientIssue: (
    ingredientAndRecipeId: string,
    issueId: string,
    issueName: string,
  ) => void
  subIssues: SubIssueType[]
  trackIngredientIssues: (ingredientIssuesInfo: IngredientIssueInfo[]) => void
}

interface IngredientInfo {
  recipeId: string
  ingredientUuid: string
  issueId?: string
  issueName?: string
  label: string
}

interface IssueType {
  id: string
  label: string
  requireDescription: boolean
}

interface SubIssueType {
  id: string
  label: string
  groupLabel: string
  requireDescription: boolean
}

interface IngredientIssueInfo {
  recipeId: string
  ingredientName: string
  issueName: string
}

const IngredientIssues: React.FC<IngredientIssuesProps> = ({
  content,
  fetchIngredientIssues,
  ingredients,
  issues,
  storeSelectedIngredientIssue,
  subIssues,
  trackIngredientIssues,
}) => {
  useEffect(() => {
    fetchIngredientIssues()
  }, [fetchIngredientIssues])

  const continueHandler = useCallback((): void => {
    const ingredientIssuesInfo: IngredientIssueInfo[] = Object.values(ingredients).map(
      ({ recipeId, label, issueName }) => ({
        recipeId,
        ingredientName: label,
        issueName: issueName || '',
      }),
    )

    trackIngredientIssues(ingredientIssuesInfo)

    const hasMyIssueNotListed = Object.values(ingredients).some(
      (ingredient) =>
        ingredient.issueName === 'Other' || ingredient.issueName === "My issue isn't listed",
    )

    if (hasMyIssueNotListed) {
      browserHistory.push(`${client.getHelp.index}/${client.getHelp.contact}`)
    } else {
      browserHistory.push(`${client.getHelp.index}/${client.getHelp.ingredientReasons}`)
    }
  }, [ingredients, trackIngredientIssues])

  const changeHandler = useCallback(
    (ingredientAndRecipeId: string, issueId: string): void => {
      const selectedIssue = issues.find((issue) => issue.id === issueId)
      const selectedSubIssue = subIssues.find((subIssue) => subIssue.id === issueId)
      const issueLabel = selectedIssue ? selectedIssue.label : selectedSubIssue?.label || ''

      storeSelectedIngredientIssue(ingredientAndRecipeId, issueId, issueLabel)
    },
    [issues, subIssues, storeSelectedIngredientIssue],
  )

  return (
    <IngredientIssuesPresentation
      content={content}
      ingredients={ingredients}
      issues={issues}
      subIssues={subIssues}
      continueHandler={continueHandler}
      changeHandler={changeHandler}
    />
  )
}

export { IngredientIssues }
